import React, { useRef, useCallback, useState } from 'react';
import Select, { ValueType } from 'react-select';
import { toast } from 'react-toastify';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';

import { FightAction } from '../../../models/fight';
import Button from '../../Button';
import {
  FormLine,
  FormStructure,
  FormLabel,
  Input,
  selectStyles,
  FormElement,
} from './styles';
import labels from '../../../utils/labels';
import plurals from '../../../utils/plurals';
import { useAuth } from '../../../hooks/Auth';
import { useSocketV1Instance } from '../../../hooks';
import { useFight } from '../../../hooks/Fight';
import { sendAction } from '../../../services/actions/action-sender';

interface UpdateActionFormProps {
  action: FightAction;
  handleCloseModal: () => void;
}

const UpdateActionForm: React.FC<UpdateActionFormProps> = ({
  action,
  handleCloseModal,
}) => {
  const { token } = useAuth();
  const { fight } = useFight();

  const { emit, isConnected } = useSocketV1Instance();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: action.name,
    stance: action.stance,
    corner: action.corner,
    authorId: action.authorId,
    targetId: action.targetId,
    round: action.round,
    roundTime: action.roundTime,
  });

  const handleChange = (field: string, value: number | string | undefined) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAuthorChange = (
    selectedOption: ValueType<
      { label: number | undefined; value: number | undefined },
      false
    >,
  ) => {
    if (!selectedOption) return;

    const newAuthorId = (selectedOption as { value: number }).value;
    setFormData((prev) => ({
      ...prev,
      authorId: newAuthorId,
      targetId: prev.authorId, // Set target to previous author
    }));
  };

  const handleTargetChange = (
    selectedOption: ValueType<
      { label: number | undefined; value: number | undefined },
      false
    >,
  ) => {
    if (!selectedOption) return;

    const newTargetId = (selectedOption as { value: number }).value;
    setFormData((prev) => ({
      ...prev,
      targetId: newTargetId,
      authorId: prev.targetId, // Set author to previous target
    }));
  };
  const formRef = useRef<FormHandles>(null);

  const handleSubmit = useCallback(async () => {
    setLoading(true);
    try {
      const recordData = { ...action, ...formData };

      if (isConnected) {
        emit('record', token, recordData); 
      }

      // Mantém o `record` do comportamento atual (o formulário regrava a ação
      // e recarrega a página); o promotionId faltava, o que deixava os
      // client_sp fora do broadcast.
      await sendAction('record', {
        promotionId: fight.promotionId,
        data: recordData,
      });

      toast.success('Ação atualizada com sucesso');
      window.location.reload();
    } catch (error) {
      toast.error('Falaha ao atualizar a action');
    } finally {
      setLoading(false);
      handleCloseModal();
    }
  }, [action, formData, token, fight, handleCloseModal, isConnected, emit]);
  return (
    <>
      <FormStructure>
        <Form ref={formRef} onSubmit={handleSubmit} initialData={action}>
          <FormLine>
            <FormElement>
              <FormLabel> Nome </FormLabel>
              <Select
                name="name"
                defaultValue={{
                  label: labels[plurals[formData.name]],
                  value: action.name,
                }}
                options={Object.entries(plurals).map(([label, value]) => ({
                  label: labels[value],
                  value: label,
                }))}
                placeholder="nome"
                styles={selectStyles}
                onChange={(selected) => handleChange('name', selected?.value)}
              />
            </FormElement>
            <FormElement>
              <FormLabel> Round </FormLabel>
              <Input
                name="round"
                defaultValue={action.round}
                type="number"
                min="1"
                placeholder="round"
                onChange={(e) => handleChange('round', e.target.value)}
              />
            </FormElement>
          </FormLine>

          <FormLine>
            <FormElement>
              <FormLabel> Tempo do Round </FormLabel>
              <Input
                name="roundTime"
                defaultValue={formData.roundTime}
                type="number"
                min="0"
                placeholder="tempo do round"
                onChange={(e) => handleChange('roundTime', e.target.value)}
              />
            </FormElement>
            <FormElement>
              <FormLabel> Posição </FormLabel>
              <Select
                styles={selectStyles}
                options={[
                  { label: 'em pé', value: 'standing' },
                  { label: 'grade', value: 'fence' },
                  { label: 'chão', value: 'ground' },
                  { label: 'riding', value: 'riding' },
                ]}
                name="stance"
                defaultValue={{
                  value: action.stance,
                  label: action.stance,
                }}
                placeholder="posição"
                onChange={(selected) => handleChange('stance', selected?.value)}
              />
            </FormElement>
          </FormLine>

          <FormLine>
            <FormElement>
              <FormLabel> Id do Autor </FormLabel>
              <Select
                name="authorId"
                options={[
                  {
                    label: action.authorId ?? undefined,
                    value: action.authorId ?? undefined,
                  },
                  {
                    label: action.targetId ?? undefined,
                    value: action.targetId ?? undefined,
                  },
                ]}
                styles={selectStyles}
                value={{
                  label: formData.authorId ?? undefined,
                  value: formData.authorId ?? undefined,
                }}
                onChange={handleAuthorChange}
                placeholder="id do autor"
              />
            </FormElement>
            <FormElement>
              <FormLabel> Id do Target </FormLabel>
              <Select
                name="targetId"
                options={[
                  {
                    label: action.authorId ?? undefined,
                    value: action.authorId ?? undefined,
                  },
                  {
                    label: action.targetId ?? undefined,
                    value: action.targetId ?? undefined,
                  },
                ]}
                styles={selectStyles}
                value={{
                  label: formData.targetId ?? undefined,
                  value: formData.targetId ?? undefined,
                }}
                onChange={handleTargetChange}
                placeholder="id do target"
              />
            </FormElement>
          </FormLine>

          <FormLine>
            <FormElement>
              <FormLabel> Corner </FormLabel>
              <Select
                styles={selectStyles}
                options={[
                  { label: 'vermelho', value: 'red' },
                  { label: 'azul', value: 'blue' },
                ]}
                defaultValue={{
                  label: action.corner,
                  value: action.corner,
                }}
                onChange={(selected) => handleChange('corner', selected?.value)}
                name="corner"
                placeholder="corner"
              />
            </FormElement>
          </FormLine>

          <Button type="submit">{loading ? 'Loading...' : 'Salvar'}</Button>
        </Form>
      </FormStructure>
    </>
  );
};

export default UpdateActionForm;
